/**
 * AgentThread domain types matching backend roboto.ai.chat module
 */

/**
 * Metadata about an available model profile, returned by the API.
 */
export interface ModelProfileResponse {
  id: string;
  label: string;
  description: string;
  is_default: boolean;
}

/**
 * Enumeration of possible roles in a thread conversation.
 */
export enum AgentThreadRole {
  USER = "user",
  ASSISTANT = "assistant",
  ROBOTO = "roboto",
}

/**
 * Enumeration of possible message generation states.
 */
export enum AgentThreadMessageStatus {
  NOT_STARTED = "not_started",
  GENERATING = "generating",
  COMPLETED = "completed",
  FAILED = "failed",
  CANCELLED = "cancelled",
}

/**
 * Who is allowed to read an AgentThread after it has been created.
 *
 * Mirrors backend ``ThreadVisibility``. ``PRIVATE`` restricts reads to the
 * ``created_by`` user and Roboto admins; ``ORG`` opens the thread to every
 * member of ``org_id``. Wire values are lowercase and match the database's
 * ``CHECK (visibility IN ('private', 'org'))`` constraint from chats_019.
 *
 * Co-located with the rest of the AgentThread domain so threads-page code
 * doesn't need to reach into the Agents domain for a shape that the
 * backend models as its own enum.
 */
export enum ThreadVisibility {
  PRIVATE = "private",
  ORG = "org",
}

/**
 * Lifecycle state of a thread session — mirrors backend ``AgentSessionStatus``.
 */
export enum AgentThreadStatus {
  NOT_STARTED = "not_started",
  USER_TURN = "user_turn",
  ROBOTO_TURN = "roboto_turn",
  CLIENT_TOOL_TURN = "client_tool_turn",
  /**
   * The runner exhausted its corrective re-prompt budget without satisfying
   * every declared goal for the latest turn; the session needs human
   * intervention before it can continue.
   */
  GOALS_FAILED = "goals_failed",
}

/**
 * Whether the backend still owes work on this thread, so new messages can
 * still arrive and polling will eventually resolve. True only for the two
 * "in-flight" states: the server is generating (`ROBOTO_TURN`) or a client
 * tool is mid-execution (`CLIENT_TOOL_TURN`).
 *
 * Every other state is idle or terminal: `USER_TURN`, `NOT_STARTED`, and the
 * permanent `GOALS_FAILED`, which needs human intervention to leave. Callers
 * that refetch while a thread is active must gate on this rather than on
 * `!== USER_TURN`; the latter polls `GOALS_FAILED` and `NOT_STARTED`
 * indefinitely, since those never transition on their own.
 */
export function isActiveThreadStatus(
  status: AgentThreadStatus | undefined,
): boolean {
  return (
    status === AgentThreadStatus.ROBOTO_TURN ||
    status === AgentThreadStatus.CLIENT_TOOL_TURN
  );
}

/**
 * Lifecycle of a per-turn declared goal — mirrors backend ``AgentGoalStatus``.
 * Goals begin PENDING, then transition to ACHIEVED or FAILED (terminal).
 */
export enum AgentThreadGoalStatus {
  PENDING = "pending",
  ACHIEVED = "achieved",
  FAILED = "failed",
}

/**
 * Customer-visible read shape of a goal declared on an agent session — mirrors
 * backend ``AgentSessionGoalRecord``. Goals are declared per-turn (attached
 * to a USER-role message) and the runner enforces achievement before the turn
 * completes.
 */
export interface AgentThreadGoalRecord {
  /**
   * Discriminator selecting which goal type this row's data conforms to,
   * e.g. ``"dataset_summary"``.
   */
  goal_type: string;
  /** Validated goal payload; shape determined by ``goal_type``. */
  goal_data: Record<string, unknown>;
  /** Current lifecycle state of the goal. */
  status: AgentThreadGoalStatus;
  /**
   * Index in the session's full messages list of the USER-role message that
   * declared this goal. Use to render goal chips on the right turn.
   */
  message_sequence_num: number;
  /** Timestamp when the goal was registered. */
  created: string;
  /**
   * Timestamp when the goal transitioned to a terminal state, or null while
   * still PENDING.
   */
  concluded_at?: string | null;
}

/**
 * Enumeration of different types of content within thread messages.
 */
export enum AgentThreadContentType {
  TEXT = "text",
  TOOL_USE = "tool_use",
  TOOL_RESULT = "tool_result",
  ERROR = "error",
}

/**
 * Text content within a thread message.
 */
export interface AgentThreadTextContent {
  text: string;
}

export const isAgentThreadTextContent = (
  obj: unknown,
): obj is AgentThreadTextContent => {
  return typeof obj === "object" && obj !== null && "text" in obj;
};

/**
 * Tool usage request content within a thread message.
 */
export interface AgentThreadToolUseContent {
  content_type: "tool_use";
  tool_name: string;
  tool_use_id: string;
  /** Parsed tool input parameters chosen by the LLM (provider-agnostic). */
  input?: Record<string, unknown>;
  raw_request?: Record<string, unknown>;
}

export const isAgentThreadToolUseContent = (
  obj: unknown,
): obj is AgentThreadToolUseContent => {
  return (
    typeof obj === "object" &&
    obj !== null &&
    "content_type" in obj &&
    obj.content_type === "tool_use"
  );
};

/**
 * Tool execution result content within a thread message.
 */
export interface AgentThreadToolResultContent {
  content_type: "tool_result";
  tool_name: string;
  tool_use_id: string;
  runtime_ms: number;
  status: string;
  raw_response?: Record<string, unknown>;
}

export const isAgentThreadToolResultContent = (
  obj: unknown,
): obj is AgentThreadToolResultContent => {
  return (
    typeof obj === "object" &&
    obj !== null &&
    "content_type" in obj &&
    obj.content_type === "tool_result"
  );
};

export interface AgentThreadToolDetailResponse {
  tool_use: AgentThreadToolUseContent;
  tool_result: AgentThreadToolResultContent;
}

/**
 * Error content within a thread message.
 * Used when message generation fails due to an error or is cancelled by the user.
 */
export interface AgentThreadErrorContent {
  content_type: "error";
  /** User-friendly error message describing what went wrong. */
  error_message: string;
  /** Optional error code for programmatic handling. */
  error_code?: string;
}

export const isAgentThreadErrorContent = (
  obj: unknown,
): obj is AgentThreadErrorContent => {
  return (
    typeof obj === "object" &&
    obj !== null &&
    "content_type" in obj &&
    obj.content_type === "error"
  );
};

/**
 * Type alias for all possible content types within thread messages.
 */
export type AgentThreadContent =
  | AgentThreadTextContent
  | AgentThreadToolUseContent
  | AgentThreadToolResultContent
  | AgentThreadErrorContent;

/**
 * What the Roboto client (e.g. the Web UI) is currently viewing when the
 * user composed a message. Passed to the agent as implicit context for
 * resolving deictic references like "this dataset" or "those files" without
 * requiring the user to spell them out. Informational only — does not gate
 * authorization or scope tools.
 */
export interface ClientViewingContext {
  /**
   * IDs of datasets the user is currently viewing or has selected.
   */
  dataset_ids: string[];

  /**
   * IDs of files the user is currently viewing or has selected.
   */
  file_ids: string[];

  /**
   * State of the visualizer, when the user composed the message from the
   * visualizer view. A relatively opaque JSON blob.
   */
  visualizer_state?: Record<string, unknown>;

  /**
   * Miscellaneous client-supplied context that doesn't fit the typed fields
   * above.
   */
  misc_context?: Record<string, unknown>;
}

/**
 * A single message within a thread conversation.
 */
export interface AgentThreadMessage {
  /** The role of the message sender (user, assistant, or roboto). */
  role: AgentThreadRole;

  /** List of content blocks that make up this message. */
  content: AgentThreadContent[];

  /** Current generation status of this message. */
  status?: AgentThreadMessageStatus;

  /** ISO timestamp when this message was created. */
  created?: string;
}

/**
 * Complete record of an agent thread.
 */
export interface AgentThreadRecord {
  /** Unique identifier for this agent thread. */
  thread_id: string;

  /** Timestamp when the thread was created (ISO string). */
  created: string;

  /** User ID of the person who created this thread. */
  created_by: string;

  /** Organization ID that owns this thread. */
  org_id: string;

  /** Complete list of messages in the conversation. */
  messages: AgentThreadMessage[];

  /** Token used for incremental updates and synchronization. */
  continuation_token: string;

  /** Current status of the thread. */
  status: AgentThreadStatus;

  /**
   * Who can read this thread. ``PRIVATE`` restricts reads to the
   * ``created_by`` user and Roboto admins; ``ORG`` opens it to every
   * member of ``org_id``. Backend always serializes this field with a
   * default of ``PRIVATE`` (see ``ThreadVisibility`` in the SDK), so the
   * field is required on the wire.
   */
  visibility: ThreadVisibility;

  /** Optional title for the thread. */
  title?: string;

  /** Model profile used for this thread. */
  model_profile?: string;

  /** If this thread was forked from another, the source thread's id. */
  forked_from_thread_id?: string | null;

  /** Message sequence number in the source thread that this fork was taken from. */
  forked_from_message_sequence_num?: number | null;

  /**
   * Agent that launched this thread, when the thread was started via
   * ``POST /v1/ai/agents/<agent_id>/launch``. Threads created directly
   * with ``POST /v1/ai/threads`` (no Agent template) have this field
   * null/undefined. Audit-only — the column is intentionally not a FK
   * server-side so the lineage survives the referenced Agent being
   * renamed or deleted.
   */
  created_from_agent_id?: string | null;

  /**
   * Goals declared across this thread's turns, ordered by allocation. ``null``
   * or ``undefined`` means goals were not loaded for this record (lightweight
   * reads may omit; full-thread reads populate); an empty array means the
   * thread never declared any goals.
   */
  goals?: AgentThreadGoalRecord[] | null;
}

/**
 * Incremental update to an agent thread.
 */
export interface AgentThreadRecordDelta {
  /** New or updated messages indexed by their position in the conversation. */
  messages_by_idx: Record<number, AgentThreadMessage>;

  /** Updated token for the next incremental synchronization. */
  continuation_token: string;

  /** Updated status of the thread. */
  status?: AgentThreadStatus;

  /** Updated title of the thread. */
  title?: string;

  /**
   * Latest snapshot of all goals declared in the thread. ``null`` or
   * ``undefined`` means "no change since the last delta the BE sent" — retain
   * the previously held snapshot. An empty array means the thread has no
   * declared goals; a populated array is the authoritative latest snapshot.
   */
  goals?: AgentThreadGoalRecord[] | null;
}

// ClientToolSpec is canonically defined in clientTools/ClientTools.ts.
// Import for local use and re-export so consumers that import from AgentThreadRecord
// continue to work without changes.
import { type ClientToolSpec } from "./clientTools/ClientTools";
export { type ClientToolSpec };

/**
 * Spec for invoking a published skill as part of a turn trigger. Embedded in
 * StartAgentThreadRequest and SendMessageRequest. Server resolves access (org / archived /
 * private) and version selection.
 */
export interface InvokeSkillSpec {
  skill_id: string;
  /** Optional version number. Must exist on the skill (any version is invokable).
   * Omit to use the latest (MAX(version)) row. Note this is the structural
   * latest, not the caller's pinned ai_version — manual chip invocation
   * always runs MAX regardless of pin. */
  version?: number | null;
}

/**
 * Request payload for starting a new thread session.
 */
export interface StartAgentThreadRequest {
  /** Optional client-viewing context describing what the user is looking at. */
  client_context?: ClientViewingContext;

  /** Initial messages to start the conversation with. May be empty when at
   * least one of `goals` or `invoke_skills` is supplied; in that case the
   * server synthesizes a minimal user message so the LLM has a turn-initiating
   * prompt. */
  messages: AgentThreadMessage[];

  /** Optional system prompt to customize AI assistant behavior. */
  system_prompt?: string;

  /** Optional model profile ID for the thread session. */
  model_profile?: string;

  /** Optional client-side tools available for the LLM to invoke. */
  client_tools?: ClientToolSpec[];

  /** Skills to invoke at session start, in order. For each entry the server fabricates
   * a `load_skill` tool_use + tool_result pair in the transcript after any seeded
   * messages. Pass a single-element list for the common "invoke one skill" case. */
  invoke_skills?: InvokeSkillSpec[];
}

/**
 * Request payload for sending a new message in a thread session.
 */
export interface SendMessageRequest {
  /** Optional client-viewing context describing what the user is looking at. */
  client_context?: ClientViewingContext;
  /** Message to send. Optional when at least one of `goals` or `invoke_skills`
   * is supplied; in that case the server synthesizes a minimal user message
   * if needed (goals attach to it; chips alone trigger the turn). */
  message?: AgentThreadMessage;
  /** Optional client-side tools available for the LLM to invoke. */
  client_tools?: ClientToolSpec[];
  /** Skills to invoke as part of this turn, in order. Author-only and only allowed
   * during USER_TURN. For each entry the server fabricates a `load_skill` tool_use +
   * tool_result pair in the transcript after `message` (if any). Pass a
   * single-element list for the common "invoke one skill" case. */
  invoke_skills?: InvokeSkillSpec[];
}

/**
 * Result of a client-side tool execution, submitted by the browser.
 *
 * Uses `output` for the tool's structured domain response (provider-agnostic),
 * as opposed to `AgentThreadToolResultContent.raw_response` which may hold
 * provider-specific payloads from the persistence layer.
 */
export interface ClientToolResult {
  /** Identifier of the tool invocation this result corresponds to. */
  tool_use_id: string;
  /** Name of the tool that was executed. */
  tool_name: string;
  /** Wall-clock execution time of the tool in milliseconds. */
  runtime_ms: number;
  /** Outcome of the tool execution. */
  status: "success" | "error";
  /** Structured output returned by the tool. */
  output?: Record<string, unknown>;
}

/**
 * Request payload for submitting client-side tool execution results.
 */
export interface SubmitToolResultsRequest {
  /** Results from executing client-side tools. */
  tool_results: ClientToolResult[];
  /** Optional updated list of client-side tools available for subsequent turns. */
  client_tools?: ClientToolSpec[];
}

/**
 * Overall direction of a piece of AI-thread feedback.
 */
export enum FeedbackSentiment {
  POSITIVE = "positive",
  NEGATIVE = "negative",
}

/**
 * Taxonomy of feedback categories. Which categories are valid depends on the
 * selected sentiment; ``OTHER`` is always valid (with required notes).
 */
export enum FeedbackCategory {
  // Negative
  INCORRECT = "incorrect",
  INCOMPLETE = "incomplete",
  TOOL_FAILURE = "tool_failure",
  REFUSED_VALID_REQUEST = "refused_valid_request",
  FORMATTING = "formatting",
  SLOW = "slow",
  UNSAFE = "unsafe",
  // Positive
  CORRECT = "correct",
  HELPFUL = "helpful",
  GOOD_TOOL_USE = "good_tool_use",
  // Either
  OTHER = "other",
}

export const NEGATIVE_CATEGORIES: ReadonlyArray<FeedbackCategory> = [
  FeedbackCategory.INCORRECT,
  FeedbackCategory.INCOMPLETE,
  FeedbackCategory.TOOL_FAILURE,
  FeedbackCategory.REFUSED_VALID_REQUEST,
  FeedbackCategory.FORMATTING,
  FeedbackCategory.SLOW,
  FeedbackCategory.UNSAFE,
];

export const POSITIVE_CATEGORIES: ReadonlyArray<FeedbackCategory> = [
  FeedbackCategory.CORRECT,
  FeedbackCategory.HELPFUL,
  FeedbackCategory.GOOD_TOOL_USE,
];

/**
 * Human-readable label for a feedback category. Single source of truth — the
 * wizard, the admin filter dropdown, and the admin table all render via this
 * map so labels can't drift apart.
 */
export const FEEDBACK_CATEGORY_LABELS: Record<FeedbackCategory, string> = {
  // Negative
  [FeedbackCategory.INCORRECT]: "Incorrect / hallucinated",
  [FeedbackCategory.INCOMPLETE]: "Incomplete",
  [FeedbackCategory.TOOL_FAILURE]: "Tool failure",
  [FeedbackCategory.REFUSED_VALID_REQUEST]: "Refused valid request",
  [FeedbackCategory.FORMATTING]: "Formatting",
  [FeedbackCategory.SLOW]: "Slow",
  [FeedbackCategory.UNSAFE]: "Unsafe",
  // Positive
  [FeedbackCategory.CORRECT]: "Correct",
  [FeedbackCategory.HELPFUL]: "Helpful",
  [FeedbackCategory.GOOD_TOOL_USE]: "Good tool use",
  // Either
  [FeedbackCategory.OTHER]: "Other",
};

/**
 * Request payload for submitting feedback on an assistant message.
 *
 * One feedback row exists per (thread, message, user). Resubmitting from the
 * same user replaces sentiment/categories/notes in place.
 */
export interface SubmitFeedbackRequest {
  sentiment: FeedbackSentiment;
  /** Zero or more categories. ``OTHER`` requires ``notes``. */
  categories: FeedbackCategory[];
  notes?: string | null;
}

/**
 * Admin-only triage update payload.
 *
 * Fields left undefined are not modified. ``admin_label`` and ``admin_note``
 * may be set to ``null`` to clear the stored value. ``resolved`` is a non-null
 * boolean column; toggle it with ``true``/``false`` (which stamps/clears
 * ``resolved_at``/``resolved_by`` server-side) — ``null`` is rejected.
 */
export interface AdminUpdateFeedbackRequest {
  admin_label?: string | null;
  admin_note?: string | null;
  resolved?: boolean;
}

/**
 * Customer-facing projection of a feedback record.
 *
 * Returned by non-admin endpoints (`POST /v1/ai/threads/<id>/messages/<idx>/feedback`
 * and `GET /v1/ai/threads/<id>/feedback`). The wire shape carries no
 * `admin_label`, `admin_note`, `resolved`, `resolved_by`, or `resolved_at`
 * for non-admin callers — the privacy guarantee lives at the route boundary
 * (server-side `UserFeedbackRecord.from_admin_record`), not in the TypeScript
 * type system: because `AgentFeedbackRecord` extends this interface, an
 * admin record IS structurally assignable to a `UserFeedbackRecord`. The
 * type system can't stop a route from leaking; the route itself must.
 */
export interface UserFeedbackRecord {
  feedback_id: string;
  thread_id: string;
  message_sequence_num: number;
  org_id: string;
  created: string;
  created_by: string;
  modified: string;
  modified_by: string;
  sentiment: FeedbackSentiment;
  categories: FeedbackCategory[];
  notes?: string | null;
}

/**
 * Admin view of a feedback record. Used only by admin endpoints. Extends
 * `UserFeedbackRecord` with the triage columns; structurally assignable to
 * the user record so the type system does not enforce the projection — see
 * the comment above `UserFeedbackRecord` for where the boundary actually is.
 */
export interface AgentFeedbackRecord extends UserFeedbackRecord {
  admin_label?: string | null;
  admin_note?: string | null;
  resolved: boolean;
  resolved_by?: string | null;
  resolved_at?: string | null;
}
